import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const WelcomeModal = ({ onClose }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);

    const steps = [
        {
            emoji: '🎉',
            titleKey: 'welcome_step1_title',
            descKey: 'welcome_step1_desc',
            title: 'Welcome to Gonkle!',
            desc: 'Find local sports events and connect with people nearby.'
        },
        {
            emoji: '📍',
            titleKey: 'welcome_step2_title',
            descKey: 'welcome_step2_desc',
            title: 'Create Events',
            desc: 'Tap the map or the + button to create an event at any location.'
        },
        {
            emoji: '⚽',
            titleKey: 'welcome_step3_title',
            descKey: 'welcome_step3_desc',
            title: 'Choose Your Sport',
            desc: 'Browse through different sports and join events that interest you.'
        },
        {
            emoji: '💬',
            titleKey: 'welcome_step4_title',
            descKey: 'welcome_step4_desc',
            title: 'Chat & Connect',
            desc: 'Use the ME menu to manage your events and chat with participants.'
        }
    ];

    const currentStep = steps[step];
    const isLastStep = step === steps.length - 1;

    const handleNext = () => {
        if (isLastStep) {
            onClose();
        } else {
            setStep(step + 1);
        }
    };

    const handleSkip = () => {
        onClose();
    };

    return (
        <div
            className="modal-backdrop"
            onClick={handleSkip}
            style={{
                zIndex: 2000,
                background: 'rgba(0, 0, 0, 0.85)'
            }}
        >
            <motion.div
                className="modal-content"
                initial={{ scale: 0.5, opacity: 0, y: 100 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.5, opacity: 0, y: -100 }}
                transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 20,
                    duration: 0.5
                }}
                onClick={e => e.stopPropagation()}
                style={{
                    maxWidth: '450px',
                    width: '90%',
                    textAlign: 'center',
                    position: 'relative'
                }}
            >
                {/* Close X button */}
                <button
                    onClick={handleSkip}
                    style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.8em',
                        cursor: 'pointer',
                        padding: '5px 10px',
                        color: 'var(--color-text-primary)',
                        fontWeight: 'bold',
                        lineHeight: '1',
                        opacity: 0.7
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                >
                    ✕
                </button>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        style={{ padding: '20px 0' }}
                    >
                        <div style={{ fontSize: '5em', marginBottom: '20px' }}>
                            {currentStep.emoji}
                        </div>

                        <h2 style={{
                            fontFamily: 'var(--font-display)',
                            fontSize: '2em',
                            marginBottom: '15px',
                            color: 'var(--color-text-primary)'
                        }}>
                            {t(currentStep.titleKey) || currentStep.title}
                        </h2>

                        <p style={{
                            fontSize: '1.1em',
                            lineHeight: '1.6',
                            color: 'var(--color-text-secondary)',
                            marginBottom: '30px'
                        }}>
                            {t(currentStep.descKey) || currentStep.desc}
                        </p>
                    </motion.div>
                </AnimatePresence>

                {/* Step indicators */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '25px'
                }}>
                    {steps.map((_, index) => (
                        <div
                            key={index}
                            style={{
                                width: step === index ? '24px' : '8px',
                                height: '8px',
                                borderRadius: '4px',
                                background: step === index
                                    ? 'var(--color-accent)'
                                    : 'var(--color-accent-dim)',
                                transition: 'all 0.3s ease'
                            }}
                        />
                    ))}
                </div>

                {/* Navigation buttons */}
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'center'
                }}>
                    {!isLastStep && (
                        <button
                            onClick={handleSkip}
                            className="button-secondary"
                        >
                            {t('welcome_skip') || 'Skip'}
                        </button>
                    )}

                    <button
                        onClick={handleNext}
                        className="button-primary"
                    >
                        {isLastStep
                            ? (t('welcome_start') || "Let's Go!")
                            : (t('welcome_next') || 'Next')
                        }
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default React.memo(WelcomeModal);
